/**
 * Sign-in.
 *
 * Collapses three legacy entry points into one form. `handleDoctorLogin`,
 * `handlePatientAuth` and `handleAdminLogin` all called the same
 * POST /api/auth/login and differed only in which modal they lived in and which
 * roles they then rejected — so a doctor typing into the patient box was told
 * "invalid permission" despite valid credentials. One form, and the role in the
 * response decides where the user lands.
 */

import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button, Field, Icon, Input, Panel } from '../../components/common';
import { ROLE_HOME, useAuth } from '../../context/AuthContext';
import { validateLogin } from '../../domain/validation';
import { useGoogleSignIn, GOOGLE_ROLES } from '../../hooks/useGoogleSignIn';

export function LoginPage() {
  const { login, loginWithGoogleResult, isAuthenticated, homePath } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [form, setForm] = useState({ username: '', password: '' });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [googleNotice, setGoogleNotice] = useState(null);

  const redirectTo = location.state?.from || null;

  const google = useGoogleSignIn({
    onAuthenticated: (result) => {
      const session = loginWithGoogleResult(result);
      if (session) navigate(redirectTo || ROLE_HOME[session.role] || '/', { replace: true });
    },
    onError: (err) => setServerError(err.message),
  });

  if (isAuthenticated) return <Navigate to={redirectTo || homePath} replace />;

  const update = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
    setServerError(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validation = validateLogin(form);
    setErrors(validation.errors);
    if (!validation.valid) return;

    setSubmitting(true);
    setServerError(null);
    try {
      const session = await login(form.username.trim(), form.password);
      navigate(redirectTo || ROLE_HOME[session.role] || '/', { replace: true });
    } catch (err) {
      // The backend's 403 messages are the actionable ones (suspended account,
      // pending approval), so they are shown as-is rather than generalised.
      setServerError(err.message || 'بيانات الدخول غير صحيحة');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoleChoice = async (role) => {
    const result = await google.completeRegistration(role);
    if (result?.pending) {
      setGoogleNotice('تم تسجيل طلبك كطبيب بنجاح. يرجى انتظار موافقة الإدارة.');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center app-backdrop p-4">
      <div className="w-full max-w-md">
        {/* The agency mark, above the card. `onError` hides it rather than
            leaving a broken-image box, which is what the legacy markup did. */}
        <div className="mb-6 flex justify-center">
          <img
            src="/abbas-jr-developments.png"
            alt="ABBAS JR Developments"
            onError={(event) => {
              event.currentTarget.style.display = 'none';
            }}
            className="brand-mark logo-glow-lg h-28 object-contain opacity-95 sm:h-32"
          />
        </div>

        <Panel className="border-t-4 border-t-cyan-500 shadow-2xl shadow-cyan-500/10">
          {/* The product mark, in its glowing well */}
          <div className="mb-6 flex flex-col items-center text-center">
            <div className="neon-glow mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-cyan-500/10">
              <img
                src="/capsule-logo.png"
                alt="كبسولة"
                onError={(event) => {
                  event.currentTarget.style.display = 'none';
                }}
                className="h-10 w-10 object-contain"
              />
            </div>
            <h1 className="text-2xl font-bold text-white">مرحباً بك في كبسولة</h1>
            <p className="mt-1 text-sm text-slate-400">منصة الرعاية الصحية</p>
          </div>

          {google.pendingProfile ? (
            <div className="space-y-4">
              <div className="text-center">
                <h2 className="text-lg font-bold text-white">اختر نوع الحساب</h2>
                <p className="mt-1 text-sm text-slate-400">
                  {google.pendingProfile.email}
                </p>
              </div>

              <div className="space-y-2">
                {GOOGLE_ROLES.map((role) => (
                  <button
                    key={role.value}
                    type="button"
                    disabled={google.busy}
                    onClick={() => handleRoleChoice(role.value)}
                    className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-slate-900 p-4 text-sm font-bold text-white transition-colors hover:border-cyan-500/50 hover:bg-slate-800 disabled:opacity-50"
                  >
                    {role.label}
                    <Icon name="chevron-left" size={18} className="text-slate-500" />
                  </button>
                ))}
              </div>

              <Button variant="ghost" className="w-full" onClick={google.cancelRoleSelection}>
                رجوع
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <h2 className="text-lg font-bold text-white">تسجيل الدخول</h2>

              <Field label="اسم المستخدم" error={errors.username} required>
                <Input
                  value={form.username}
                  onChange={update('username')}
                  autoComplete="username"
                  placeholder="اسم المستخدم"
                  autoFocus
                />
              </Field>

              <Field label="كلمة المرور" error={errors.password} required>
                <Input
                  type="password"
                  value={form.password}
                  onChange={update('password')}
                  autoComplete="current-password"
                  placeholder="••••••••"
                />
              </Field>

              {serverError && (
                <div className="flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-xs font-bold text-red-300">
                  <Icon name="alert-circle" size={14} className="mt-0.5 shrink-0" />
                  <span>{serverError}</span>
                </div>
              )}

              {googleNotice && (
                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs font-bold text-emerald-300">
                  {googleNotice}
                </div>
              )}

              <Button type="submit" className="w-full" loading={submitting} icon="log-in">
                دخول
              </Button>

              {google.configured && (
                <>
                  <div className="flex items-center gap-3 py-1">
                    <span className="h-px flex-1 bg-white/10" />
                    <span className="text-[11px] text-slate-500">أو</span>
                    <span className="h-px flex-1 bg-white/10" />
                  </div>
                  <div ref={google.buttonRef} className="flex justify-center" />
                </>
              )}

              <p className="pt-2 text-center text-xs text-slate-400">
                ليس لديك حساب؟{' '}
                <Link to="/register" className="font-bold text-cyan-400 hover:text-cyan-300">
                  إنشاء حساب جديد
                </Link>
              </p>
            </form>
          )}
        </Panel>
      </div>
    </div>
  );
}

export default LoginPage;
